import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";
import { fieldValueToString } from "../../../utils/react_hook_form_input_utils";

/**
 * FormPercentageMaskedInputBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
}

const FormPercentageMaskedInputBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      placeholderChar="_"
      inputRef={refs}
      onAccept={(value: any, input: { unmaskedValue: string }) => {
        if (input.unmaskedValue.length > 0) {
          onChange({ target: { name: props.name, value } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={true}
    />
  );
});

/**
 * FormPercentageMaskedInput
 */

type Props<T extends {}> = {
  name: keyof T,
  label: string,
  required?: boolean,
  reactHookForm: UseFormReturn<T>,
  readOnly?: boolean,
};

export const FormPercentageMaskedInput = <T extends {},>({
  name,
  label,
  required,
  reactHookForm,
  readOnly = false,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText: string | null = null;

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <FormControl variant="outlined" size="small">
            <InputLabel
              htmlFor={`input-${name as string}`}
              error={helperText !== null}
              shrink={true}
              size="small"
              required={required}>
              {label}
            </InputLabel>

            <OutlinedInput
              {...field}
              size="small"
              id={`input-${name as string}`}
              name={name as string}
              label={label}
              notched={true}
              required={required}
              readOnly={readOnly}
              disabled={formState.disabled}
              aria-describedby={`${name as string}-component-error-text`}
              inputComponent={FormPercentageMaskedInputBase as any}
              value={fieldValueToString(field.value)}
              inputProps={{
                mask: [
                  { mask: '\\0,\\0\\0 %', lazy: true },
                  { mask: '\\0,\\00 %', lazy: false },
                  { mask: '\\0,00 %', lazy: false },
                  { mask: '0,00 %', lazy: false },
                  { mask: '00,00 %', lazy: false },
                  { mask: '000,00 %', lazy: false },
                ],
                placeholder: "___,__ %",
                required: required,
              }} />

            <FormHelperText id={`${name as string}-component-error-text`} error={true}>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }} />
  );
}